import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { X } from "lucide-react";
import { promo as fallback } from "@/data/promo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const pad = (n) => String(n).padStart(2, "0");

const formatRemaining = (ms) => {
  const s = Math.floor(ms / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return d > 0 ? `${d}d ${pad(h)}h ${pad(m)}m` : `${pad(h)}:${pad(m)}:${pad(sec)}`;
};

const Countdown = ({ expiresAt, onExpire }) => {
  const [remaining, setRemaining] = useState(() => new Date(expiresAt) - new Date());

  useEffect(() => {
    const tick = () => {
      const ms = new Date(expiresAt) - new Date();
      setRemaining(ms);
      if (ms <= 0) onExpire();
    };
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiresAt, onExpire]);

  if (remaining <= 0) return null;
  return (
    <span
      data-testid="promo-countdown"
      className="font-mono text-[11px] tracking-[0.08em] bg-[#2563EB] text-white px-2 py-0.5 rounded whitespace-nowrap"
    >
      Ends in {formatRemaining(remaining)}
    </span>
  );
};

const PromoBanner = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (localStorage.getItem(fallback.storageKey) === "1") return;
    axios.get(`${API}/promo/active`)
      .then((r) => {
        if (cancelled) return;
        if (r.data && r.data.code) {
          if (r.data.expires_at && new Date(r.data.expires_at) <= new Date()) return;
          setData({
            active: true,
            code: r.data.code,
            message: `${r.data.label} — ${r.data.discount_text} with code`,
            expiresAt: r.data.expires_at || null,
            ctaText: fallback.ctaText,
            ctaTo: fallback.ctaTo,
          });
        } else if (fallback.active) {
          setData(fallback);
        }
      })
      .catch(() => { if (!cancelled && fallback.active) setData(fallback); });
    return () => { cancelled = true; };
  }, []);

  if (!data) return null;

  const dismiss = () => {
    localStorage.setItem(fallback.storageKey, "1");
    setData(null);
  };

  return (
    <div data-testid="promo-banner" className="w-full bg-[#111111] text-white">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-2.5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <span className="hidden sm:inline-block font-mono text-[10px] tracking-[0.22em] uppercase text-[#2563EB]">New</span>
          <p className="font-manrope text-[13px] md:text-[14px] truncate">
            {data.message}{" "}
            <span data-testid="promo-code" className="font-mono bg-white/10 px-1.5 py-0.5 rounded text-[12px] ml-1">{data.code}</span>
          </p>
          {data.expiresAt && (
            <Countdown expiresAt={data.expiresAt} onExpire={() => setData(null)} />
          )}
          <Link to={data.ctaTo} data-testid="promo-cta" className="font-manrope text-[13px] md:text-[14px] font-medium underline decoration-[#2563EB] decoration-2 underline-offset-2 hover:text-[#2563EB] transition-colors hidden md:inline-block whitespace-nowrap">{data.ctaText}</Link>
        </div>
        <button data-testid="promo-dismiss" onClick={dismiss} aria-label="Dismiss" className="inline-flex items-center justify-center w-7 h-7 rounded-md text-white/70 hover:text-white hover:bg-white/10 shrink-0">
          <X size={14} />
        </button>
      </div>
    </div>
  );
};

export default PromoBanner;
